import { Component, OnInit, ViewChild, ElementRef, Input, Output, EventEmitter, TemplateRef, Injector, inject, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Provider } from 'src/app/shared/models/provider';
import { Observable, timer, of, Subject, combineLatest, BehaviorSubject, forkJoin } from 'rxjs';
import { Patient, PatientWithCases } from 'src/app/shared/models/patient';
import { Agreement } from 'src/app/shared/models/agreement';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { startWith, map, filter, debounce, tap, switchAll, takeUntil, distinctUntilChanged, auditTime, take } from 'rxjs/operators';
import moment from 'moment';
import { ProvidersQPS, ProvidersService } from 'src/app/core/services/providers.service';
import { EntitiesFacade } from 'src/app/abstraction/entities.facade';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { SelectionModel } from '@angular/cdk/collections';
import { Case } from 'src/app/shared/models/case';
import { ActivatedRoute, Router } from '@angular/router';
import { ROUTES } from 'src/app/core/enums/routes';
import { ProvisionsFacade } from 'src/app/abstraction/provisions.facade';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { DialogComponent } from 'src/app/shared/components/dialog/dialog.component';
import { ProvidersManagementComponent } from 'src/app/modules/data-management/pages/providers-management/providers-management/providers-management.component';
import { AttentionsFacade } from 'src/app/abstraction/attentions.facade';
import { AlertService } from 'src/app/core/services/alert.service';
import { ExperienceI18nService } from 'src/app/core/experience/experience-i18n.service';

@Component({
    selector: 'app-filter',
    templateUrl: './filter.component.html',
    styleUrls: ['./filter.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', opacity: 0, overflow: 'hidden' })),
            state('expanded', style({ height: '*', opacity: 1, overflow: 'hidden' })),
            transition('expanded <=> collapsed', animate('230ms cubic-bezier(0.4,0,0.2,1)')),
        ]),
    ],
})
export class FilterComponent implements OnInit {

    @Input() getAgreementsByProvider!: (id: number, active: boolean | null) => Promise<Agreement[]>;
    @Input() preselectProviderId: number;

    @Output() onLoadSchedule = new EventEmitter<Agreement[]>();
    @Output() cases = new EventEmitter<Case[]>();
    @Output() onLoadScheduleWithDataFilter = new EventEmitter<any>();

    @ViewChild('providerInput', { static: true }) providerInput: ElementRef<HTMLInputElement>;
    @ViewChild('providerAuto', { static: true }) providerAuto: MatAutocomplete;
    @ViewChild('patientInput', { static: false }) patientInput: ElementRef<HTMLInputElement>;
    @ViewChild('patientAuto', { static: true }) patientAuto: MatAutocomplete;
    @ViewChild('providerDSPHost', { static: true }) providerDSPHost!: TemplateRef<any>;
    dspInjector!: Injector;
    providersManagementCmp = ProvidersManagementComponent;
    readonly MAX_SELECTED = 5;
    selection = new SelectionModel<Patient>(true, []);
    private destroy$ = new Subject<void>();
    private norm = (s: string) => (s ?? '').toLowerCase().trim();
    patients$ = new BehaviorSubject<Patient[]>([]);
    moment = moment;
    _dialogRef;
    loadingGetProviders = false;
    patientCtrl = new FormControl();
    providerCtrl = new FormControl();
    showOpenCtrl = new FormControl<boolean>(true, { nonNullable: true });
    showClosedCtrl = new FormControl<boolean>(false, { nonNullable: true });
    showInactiveAgreementsCtrl = new FormControl<boolean>(false, { nonNullable: true });
    filteredProviders: Provider[];
    filteredPatients: Observable<Patient[]>;
    loadingFilteredAllPatients = false;
    patients: Patient[] = [];
    providers: Provider[] = [];
    agreements: Agreement[] = [];
    allPatients: Patient[] = [];
    casesFiltered: Case[] = []
    providersList: Provider[] = [];
    allAgreements: Agreement[] = [];
    allAgreementsList: Agreement[] = [];
    patientsWithCases: PatientWithCases[] = [];
    filteredPatientsWithCases: PatientWithCases[] = [];
    storedValue;
    loadAttentions: boolean = false;
    providerSelected: Provider = null;
    providerQPS_Default: ProvidersQPS = {
        isSupply: false,
        isFilteredByRegion: false
    };
    filteredAllPatients: Patient[] = [];
    searchCtrl = new FormControl<string>('', { nonNullable: true });
    activeCasesFiltered: boolean = true;    //active = null **filter all ** || active = true || active = false ** filter by active or inactive
    isLoadingGetAgreementsByProvidersIds!: Signal<boolean>;
    isLoadingGettingProviders!: Signal<boolean>;
    isLoadingAttentionsProcess!: Signal<boolean>;
    scheduleForm: FormGroup;
    providerId: string | null = null;
    providerPreselected: Provider | null = null;

    private route = inject(ActivatedRoute);
    public readonly PROVIDERS_SELECTED = 'providersSelected';
    public readonly PROVIDER_SEARCHING = 'providerSearching';
    public readonly PATIENTS_SELECTED = 'patientsSelected';
    public readonly INCLUDE_INACTIVE_AGREEMENTS = 'includeInactiveAgreements';


    get providersSelected() {
        return this.scheduleForm.get(this.PROVIDERS_SELECTED)?.value;
    }
    get patientsSelected() {
        return this.scheduleForm.get(this.PATIENTS_SELECTED).value;
    }

    get includeInactiveAgreementsFlag(): boolean {
        return !!this.scheduleForm?.get(this.INCLUDE_INACTIVE_AGREEMENTS)?.value;
    }

    private expandedIds = new Set<number>();


    constructor(
        public entitiesFacade: EntitiesFacade,
        private providersService: ProvidersService,
        private provisionsFacade: ProvisionsFacade,
        private formBuilder: FormBuilder,
        private router: Router,
        public dialog: MatDialog,
        private alertService: AlertService,
        private injector: Injector,
        private attentionsFacade: AttentionsFacade,
        private experienceI18n: ExperienceI18nService
    ) {
        this.isLoadingGettingProviders = toSignal(this.entitiesFacade.isLoadingGettingProviders$(), { initialValue: false });
        this.isLoadingGetAgreementsByProvidersIds = toSignal(this.provisionsFacade.isLoadingGetAgreementsByProvidersIds$(), { initialValue: false });
        this.isLoadingAttentionsProcess = toSignal(this.attentionsFacade.getIsLoadingAttentionsProcess$(), { initialValue: false });

        this.filteredPatients = this.patientCtrl.valueChanges.pipe(
            startWith(null),
            map((patient: string | null) => patient ? this._filterPatient(patient) : this.allPatients.slice()));

        let temporalValue;

        this.providerCtrl.valueChanges
            .pipe(
                startWith(''),
                filter((value: string) => !!value && value.length > 0),
                debounce(value => (value.length > 1) ? timer(500) : timer(2500)),
                // tap(_ => this.searchingSpinner = true),
                map(value => { temporalValue = value; return value == this.storedValue ? of(this.filteredProviders) : this.getProvidersByFullName(value) }),
                tap(_ => { this.storedValue = temporalValue }),
                switchAll()
            ).subscribe(values => {
                this.filteredProviders = values
            });
    }

    ngOnInit() {
        this.providerId = this.route.snapshot.paramMap.get('providerId');

        this.scheduleForm = this.createScheduleForm();

        const providerQPS: ProvidersQPS = { ...this.providerQPS_Default };
        // Load providers light (id, name, surname)
        this.loadActiveProvidersLight(providerQPS);

        if (this.providerId) {
            this.loadPreselectedProvider(Number(this.providerId));
        }

        this.searchCtrl.valueChanges
            .pipe(startWith(''), takeUntil(this.destroy$))
            .subscribe(() => this.applyPatientsFilter());

        combineLatest([
            this.showOpenCtrl.valueChanges.pipe(startWith(this.showOpenCtrl.value)),
            this.showClosedCtrl.valueChanges.pipe(startWith(this.showClosedCtrl.value)),
        ])
            .pipe(
                map(([open, closed]) => this.computeActiveCasesFiltered(open, closed)),
                distinctUntilChanged(),
                auditTime(0),
                takeUntil(this.destroy$)
            )
            .subscribe(val => {
                if (val === undefined) {
                    return;
                }
                this.activeCasesFiltered = val;
                this.getAgreements();
            });
    }

    private mergeProviderIntoList(provider: Provider | null): void {
        if (!provider) {
            return;
        }

        const providerIndex = this.providersList.findIndex(item => item.id === provider.id);
        if (providerIndex < 0) {
            this.providersList = [provider, ...this.providersList];
            return;
        }

        this.providersList = this.providersList.map(item => item.id === provider.id ? { ...item, ...provider } : item);
    }

    private mergeProvidersById(providers: Provider[]): Provider[] {
        const seen = new Set<number>();
        return (providers ?? []).filter(provider => {
            if (!provider || seen.has(provider.id)) {
                return false;
            }

            seen.add(provider.id);
            return true;
        });
    }

    private loadProviderById(providerId: number): Observable<Provider | null> {
        return forkJoin([
            this.providersService.getProviders({ id: providerId, active: true }),
            this.providersService.getProviders({ id: providerId, active: false })
        ]).pipe(
            map(([activeResponse, inactiveResponse]) => {
                const providers = this.mergeProvidersById([
                    ...(activeResponse?.data ?? []),
                    ...(inactiveResponse?.data ?? [])
                ]);

                return providers[0] ?? null;
            })
        );
    }

    private loadPreselectedProvider(providerId: number): void {
        this.loadProviderById(providerId)
            .pipe(
                take(1)
            )
            .subscribe(provider => {
                if (!provider) {
                    return;
                }

                this.providerPreselected = provider;
                this.mergeProviderIntoList(provider);

                if (!this.providerSelected) {
                    this.selectedProvider(String(provider.id));
                }
            });
    }

    // ngOnChanges(changes: SimpleChanges): void {
    //     if (changes['preselectProviderId'] && this.preselectProviderId) {
    //         this.providersService.getProviders({ id: this.preselectProviderId })
    //             .subscribe({
    //                 next: resp => {
    //                     this.providers = resp.data;
    //                     this.providerSelected = this.providers[0];
    //                     this.selectedProvider();

    //                 },
    //                 error: err => console.error(err)
    //             });
    //     }
    // }

    isExpanded = (p: PatientWithCases) => this.expandedIds.has(p.id);

    toggleExpand(p: PatientWithCases, ev?: MouseEvent) {
        ev?.stopPropagation();
        if (this.expandedIds.has(p.id)) this.expandedIds.delete(p.id);
        else this.expandedIds.add(p.id);
    }

    rebuildPatientsWithCases(): void {
        const byPatient = new Map<number, PatientWithCases>();

        // 1) Aplicar filtro de acuerdos (activos por defecto; incluir inactivos si el flag lo permite)
        const agreements = this.allAgreements.filter(this.shouldIncludeAgreement);

        for (const ag of agreements) {
            const pat = ag?.provision?.casee?.patient as Patient;
            const cas = ag?.provision?.casee as Case;
            if (!pat || !cas) continue;

            // 2) Aplicar filtro de casos abierto/cerrado
            if (!this.shouldIncludeCase(cas)) continue;

            // 3) Bucket por paciente
            let bucket = byPatient.get(pat.id);
            if (!bucket) {
                bucket = { ...pat, cases: [] };
                byPatient.set(pat.id, bucket);
            }

            // 4) Dedupe por ID de case antes de pushear
            const idToAdd = String(cas.id);
            const exists = bucket.cases.some(x => String(x.id) === idToAdd);
            if (!exists) bucket.cases.push(cas);
        }

        // 5) Construir arrays visibles y sincronizar selección
        this.patientsWithCases = Array.from(byPatient.values())
            .sort((a, b) => this.comparePatientByName(a, b));

        this.allPatients = this.patientsWithCases.map(pwc => pwc as Patient);

        this.publishAllPatientsAndReconcile();
        // Si usás filtro por texto:
        this.applyPatientsFilter?.();
    }

    applyPatientsFilter(): void {
        const t = this.norm(this.searchCtrl.value ?? '');
        if (!t) {
            this.filteredPatientsWithCases = [...this.patientsWithCases];
        } else {
            this.filteredPatientsWithCases = this.patientsWithCases.filter(p =>
                this.norm(`${p.surname} ${p.name}`).includes(t) ||
                String(p.documentNumber ?? '').includes(t)
            );
        }
        this.filteredAllPatients = this.filteredPatientsWithCases.map(p => p as Patient);
        if (this.filteredPatientsWithCases.length >= 1) {
            this.loadingFilteredAllPatients = true
        }
    }

    createScheduleForm(provider?: Provider) {
        return this.formBuilder.group({
            [this.PROVIDERS_SELECTED]: [null],
            [this.PROVIDER_SEARCHING]: [''],
            [this.PATIENTS_SELECTED]: [[]]
        })
    }

    openExternalUrl(id: number): void {
        const url = this.router.serializeUrl(
            this.router.createUrlTree([ROUTES.PROVISIONS_CONTROL, id])
        );
        const fullUrl = `${window.location.origin}${url}`;
        window.open(fullUrl, '_blank', 'noopener,noreferrer');
    }

    calculateNameProvider = (element: Provider | null) => {
        if (!element) return null;
        return this.entitiesFacade.getProvidersFullName(element);
    };

    calculateIdProvider = (element: Provider | null) => {
        return element ? element.id : null;
    };

    // Búsqueda solo para providers activos
    loadActiveProvidersLight = (providerQPS: ProvidersQPS) => {
        this.entitiesFacade.loadProvidersLight({ ...providerQPS, active: true }).subscribe(providers => {
            this.providersList = providers ?? [];

            if (this.providerPreselected) {
                this.mergeProviderIntoList(this.providerPreselected);
            }

            if (this.providerId && !this.providerSelected) {
                this.selectedProvider(this.providerId);
            }
        });
    };

    // Búsqueda general para providers (activos e inactivos)
    loadProvidersLight = (providerQPS: ProvidersQPS) => {
        forkJoin([
            this.entitiesFacade.loadProvidersLight({ ...providerQPS, active: true }),
            this.entitiesFacade.loadProvidersLight({ ...providerQPS, active: false })
        ]).pipe(
            map(([activeProviders, inactiveProviders]) => this.mergeProvidersById([
                ...(activeProviders ?? []),
                ...(inactiveProviders ?? [])
            ]))
        ).subscribe(providers => {
            this.providersList = providers ?? [];

            if (this.providerPreselected) {
                this.mergeProviderIntoList(this.providerPreselected);
            }

            if (this.providerId && !this.providerSelected) {
                this.selectedProvider(this.providerId);
            }
        });
    };

    removePatient(patient: Patient): void {
        const index = this.patients.indexOf(patient);

        if (index >= 0) {
            this.patients.splice(index, 1);
            // Eliminar las prestaciones de ese paciente
            this.removeAgreementsListByPatient(patient.id);
        }
    }

    selectedProvider(id?: string): void {
        this.emitResetScheduleComponent();

        let providerSelected: Provider;

        if (id) {
            providerSelected =
                this.providersList.find(provider => provider.id === parseInt(id, 10)) ??
                this.providerPreselected ??
                null;
        } else {
            providerSelected = this.providers.find(provider => provider.id === this.preselectProviderId);
        }

        this.providers = providerSelected ? [providerSelected] : [];
        this.providerSelected = providerSelected;
        this.scheduleForm.get(this.PROVIDERS_SELECTED)?.setValue(providerSelected?.id ?? null, { emitEvent: false });

        if (providerSelected?.id && providerSelected.active === undefined) {
            this.loadProviderById(providerSelected.id)
                .pipe(take(1))
                .subscribe(fullProvider => {
                    if (!fullProvider) {
                        return;
                    }

                    this.providerSelected = fullProvider;
                    this.providers = [fullProvider];
                    this.mergeProviderIntoList(fullProvider);
                });
        }

        this.providerCtrl.setValue('', { emitEvent: false });
        if (this.providerInput) {
            this.providerInput.nativeElement.value = '';
        }

        this.getAgreements()
    }

    getAgreements() {
        if (!this.providerSelected) return;

        this.allAgreements = [];
        this.allAgreementsList = [];
        this.agreements = [];
        this.casesFiltered = [];

        const prom = this.getAgreementsByProvider(this.providerSelected.id, this.activeCasesFiltered);
        (prom instanceof Promise ? prom : null /* if it were Observable convert to Promise */)
            ?.then(agreementsRequest => {
                this.allAgreements = agreementsRequest ?? [];
            })
            .then(() => this.updateDataByProvider(this.providerSelected, true))
            .finally(() => {
                this.providerCtrl.setValue(null);
                this.rebuildPatientsWithCases();
            });
    }

    selectedPatient(event: MatAutocompleteSelectedEvent): void {
        const patient: Patient = event.option.value;
        if (!this.patients.find(_patient => _patient.id === patient.id)) {
            this.patients.push(patient);
            this.addAgreementListByPatient(patient)
        }
        this.patientInput.nativeElement.value = '';
        this.patientCtrl.setValue(null);
    }

    _filterPatient(value): Patient[] {
        const filterValue = value.name ? '' : value.toLowerCase();
        return this.allPatients.filter(patient => {
            const nameAndsurname = patient.name.toLowerCase() + ' ' + patient.surname.toLowerCase();
            const surnameAndName = patient.surname.toLowerCase() + ' ' + patient.name.toLowerCase();

            // return nameAndsurname.toLowerCase().indexOf(filterValue) === 0
            return patient.name.toLowerCase().includes(filterValue) || patient.surname.toLowerCase().includes(filterValue) || nameAndsurname.includes(filterValue) || surnameAndName.includes(filterValue)

        });
        // return this.allPatients.filter(patient => patient.name.toLowerCase().includes(filterValue) || patient.surname.toLowerCase().includes(filterValue));
    }

    removeAllAgreementsByProvider(providerId: number) {
        // El provider puede estar en mas de un agreement
        this.allAgreements = this.allAgreements.filter(ag => ag.provider.id !== providerId);
        this.rebuildPatientsWithCases();
    }

    removeAgreementsListByPatient(patientId: number) {
        // El paciente puede estar en mas de un agreement
        this.agreements = this.agreements.filter(
            agreement => agreement.provision.casee.patient.id !== patientId
        )
        this.allAgreementsList = this.allAgreementsList.filter(
            agreement => agreement.provision.casee.patient.id !== patientId
        )
    }

    addAgreementListByPatient(patient: Patient) {
        const agreementsByPatient = this.allAgreements.filter(
            agreement => agreement && agreement.provision && agreement.provision.casee && agreement.provision.casee.patient && agreement.provision.casee.patient.id === patient.id
        );
        agreementsByPatient.forEach(
            agreementRequest => {
                if (!this.allAgreementsList.find(_agreement => _agreement.id === agreementRequest.id)) {
                    this.allAgreementsList.push(agreementRequest);
                }
                if (!this.agreements.find(_agreement => _agreement.id === agreementRequest.id)) {
                    this.agreements.push(agreementRequest);
                }
            }
        )
        this.rebuildPatientsWithCases();
    }

    fillAllDataByProvider(provider: Provider) {
        this.allPatients = [];
        this.allAgreements.forEach(
            agreement => {
                if (!this.patients.find(patient => patient.id === agreement.provision.casee.patient.id) && agreement.provider.id === provider.id) {
                    this.patients.push(agreement.provision.casee.patient);
                }
                if (!this.allPatients.find(patient => agreement.provision.casee.patient.id == patient.id)) {
                    this.allPatients.push(agreement.provision.casee.patient);
                }
                if (!this.allAgreementsList.find(_agreement => _agreement.id === agreement.id) && agreement.provider.id === provider.id) {
                    this.allAgreementsList.push(agreement);
                    this.agreements.push(agreement);
                }
            }
        )
        this.publishAllPatientsAndReconcile();
    }

    updateDataByProvider(provider: Provider, fill: boolean) {
        if (fill) {
            this.allPatients = [];

            this.allAgreements.forEach(
                agreement => {
                    if (!this.allPatients.find(patient => agreement.provision.casee.patient.id == patient.id)) {
                        this.casesFiltered.push(agreement.provision.casee)
                        this.allPatients.push(agreement.provision.casee.patient);
                        this.cases.emit(this.casesFiltered);
                    }
                }
            )
            this.allPatients = this.dedupeCases(this.allPatients);
            this.publishAllPatientsAndReconcile();
        } else {

            if (this.allAgreements.length == 0) {
                this.patients = [];
                this.allPatients = [];
                this.agreements = [];
                this.allAgreementsList = [];
            } else {
                this.patients = this.patients.filter(patient => this.allAgreements.find(agreement => agreement.provision.casee.patient.id === patient.id))
                this.allPatients = this.allPatients.filter(patient => this.allAgreements.find(agreement => agreement.provision.casee.patient.id === patient.id))
                this.allAgreementsList = this.allAgreementsList.filter(_agreement => this.allAgreements.find(agreement => agreement.id === _agreement.id))
                this.agreements = this.agreements.filter(_agreement => this.allAgreements.find(agreement => agreement.id === _agreement.id))
            }
            this.publishAllPatientsAndReconcile();
        }
        this.rebuildPatientsWithCases();
    }

    onClickLoadSchedule() {
        this.attentionsFacade.setIsLoadingAttentionsProcess(true);
        const selectedIds = new Set(this.selectedPatientIds);
        const agreementsToEmit = this.allAgreements.filter(
            ag => selectedIds.has(ag?.provision?.casee?.patient?.id)
        );

        this.onLoadSchedule.emit(agreementsToEmit);
        this.onLoadScheduleWithDataFilter.emit({
            providersSelected: this.providers,
            patientsSelected: this.selection.selected,
            agreementsSelected: agreementsToEmit,
            allPatients: this.allPatients,
            allAgreements: this.allAgreements,
            allAgreementsList: this.allAgreementsList
        });
    }

    sendAttentionsSpinner() {
        this.loadAttentions = true
        setTimeout(() => {
            this.loadAttentions = false
        }, 1000);
    }

    // Búsqueda por nombre para providers inactivos
    getActiveProvidersByFullName = (value: string) =>
        this.providersService.getProviders({ fullName_like: value, active: true }).pipe(
            map(dataApi => dataApi.data)
        );

    // Búsqueda general por nombre para providers (activos e inactivos)
    getProvidersByFullName = (value: string) =>
        forkJoin([
            this.providersService.getProviders({ fullName_like: value, active: true }),
            this.providersService.getProviders({ fullName_like: value, active: false })
        ]).pipe(
            map(([activeResponse, inactiveResponse]) => this.mergeProvidersById([
                ...(activeResponse?.data ?? []),
                ...(inactiveResponse?.data ?? [])
            ]))
        );

    getProviderById = (id: number) =>
        forkJoin([
            this.providersService.getProviders({ id, active: true }),
            this.providersService.getProviders({ id, active: false })
        ]).pipe(
            map(([activeResponse, inactiveResponse]) => this.mergeProvidersById([
                ...(activeResponse?.data ?? []),
                ...(inactiveResponse?.data ?? [])
            ]))
        )

    calculateIdFromDate(state) {
        return state != null ? state.id : null
    }

    calculateNameFromDate(state) {
        return state != null ? state.description : null
    }

    dedupeCases(items: Patient[]): Patient[] {
        const seen = new Set<string | number>();
        return items.filter(item => {
            const id = item.id;
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
        });
    }

    get masterChecked(): boolean {
        return this.filteredAllPatients.length > 0 &&
            this.filteredAllPatients.every(p => this.selection.isSelected(p));
    }

    get masterIndeterminate(): boolean {
        const anySelected = this.filteredAllPatients.some(p => this.selection.isSelected(p));
        return anySelected && !this.masterChecked;
    }

    t  /** Maestro: selecciona hasta llenar la capacidad restante, no más. */
    toggleMaster(checked: boolean): void {
        if (checked) {
            const remaining = this.remainingCapacity();
            if (remaining <= 0) {
                // opcional: aviso
                // this.snackBar.open('Máximo 5 pacientes seleccionados', 'Ok', { duration: 2500 });
                return;
            }
            const candidates = this.filteredAllPatients.filter(p => !this.selection.isSelected(p));
            const toSelect = candidates.slice(0, remaining);

            this.selection.select(...toSelect);
            for (const p of toSelect) {
                if (!this.patients.find(x => x.id === p.id)) {
                    this.patients.push(p);
                }
                this.addAgreementListByPatient(p);
            }
        } else {
            const toDeselect = this.filteredAllPatients.filter(p => this.selection.isSelected(p));
            this.selection.deselect(...toDeselect);
            for (const p of toDeselect) {
                this.removePatient(p);
            }
        }
    }

    // Para usar en el botón o para enviar al backend
    get selectedPatients(): Patient[] {
        return this.selection.selected;
    }

    get selectedPatientIds(): Array<string | number> {
        return this.selection.selected.map(p => p.id);
    }

    publishAllPatientsAndReconcile() {
        // Reconciliá selección por ID (SelectionModel usa igualdad por referencia)
        const selectedIds = new Set(this.selection.selected.map(p => p.id));
        const newRefsToSelect = this.allPatients.filter(p => selectedIds.has(p.id));
        this.selection.clear();
        this.selection.select(...newRefsToSelect);

        // Emití
        this.patients$.next(this.allPatients);
    }

    // trackBy para performance
    trackByPatient = (_: number, p: Patient) => p.id;
    //  [0].provision.casee.patient.id & push into new array =>
    //  nueva interface : {id: number, patient:patient, patientAgreements: {name:practice.name, fromdate, lastAttDate}


    // Check maestro (aplica sobre lo filtrado)
    onMasterCheckboxChange(checked: boolean): void {
        if (checked) {
            const toSelect = this.filteredAllPatients.filter(p => !this.selection.isSelected(p));
            this.selection.select(...toSelect);
            for (const p of toSelect) {
                if (!this.patients.find(x => x.id === p.id)) {
                    this.patients.push(p);
                }
                this.addAgreementListByPatient(p);
            }
        } else {
            const toDeselect = this.filteredAllPatients.filter(p => this.selection.isSelected(p));
            this.selection.deselect(...toDeselect);
            for (const p of toDeselect) {
                this.removePatient(p);
            }
        }
    }

    nameCollator = new Intl.Collator('es', {
        sensitivity: 'base',
        numeric: true,
        ignorePunctuation: true
    });

    comparePatientByName = (a: Patient, b: Patient): number => {
        const aKey = `${a?.surname ?? ''} ${a?.name ?? ''}`.trim();
        const bKey = `${b?.surname ?? ''} ${b?.name ?? ''}`.trim();
        const res = this.nameCollator.compare(aKey, bKey);
        if (res !== 0) return res;
        // desempate estable por DNI si existiera
        return this.nameCollator.compare(String(a?.documentNumber ?? ''), String(b?.documentNumber ?? ''));
    };


    trackByCase = (_: number, c: Case) => c.id;

    /** Filtra acuerdos según flag "incluir inactivos". Por defecto, excluye inactivos. */
    shouldIncludeAgreement = (ag: Agreement): boolean => {
        if (!ag) return false;
        if (this.includeInactiveAgreementsFlag) return true;
        return !!ag.active; // solo activos por defecto
    };

    /** Filtra casos según abierto/cerrado. Por defecto: solo abiertos. */
    shouldIncludeCase = (c: Case): boolean => {
        if (!c) return false;
        const isOpen = !!c.active;
        const wantOpen = this.showOpenCtrl.value;
        const wantClosed = this.showClosedCtrl.value;

        // Solo abiertos por defecto (true/false)
        if (isOpen && wantOpen) return true;
        if (!isOpen && wantClosed) return true;
        return false;
    };

    computeActiveCasesFiltered(open: boolean, closed: boolean): boolean | null | undefined {
        // (true,false)  => true
        if (open && !closed) return true;
        // (true,true)   => null
        if (open && closed) return null;
        // (false,true)  => false
        if (!open && closed) return false;
        // (false,false) => NO HACER NADA (no GET)
        return undefined;
    }

    openReadOnly(providerId: number) {
        if (!this.providerSelected) {
            return;
        }

        const qps: ProvidersQPS = {
            id: this.providerSelected.id,
            active: this.providerSelected.active
        };

        // Get providier full data by id
        this.entitiesFacade.loadProviders(qps);

        this.dspInjector = Injector.create({
            providers: [
                { provide: MAT_DIALOG_DATA, useValue: { mode: 'DSP', providerId } }
            ],
            parent: this.injector
        });

        this._dialogRef = this.dialog.open(DialogComponent, {
            disableClose: true,
            panelClass: 'dm-dsp-dialog',
            minWidth: '80%',
            maxHeight: '90%',
            data: {
                template: this.providerDSPHost,
                title: `${this.providerSelected.name} - ${this.providerSelected.surname}`,
            }
        });
    }


    remainingCapacity(): number {
        return Math.max(0, this.MAX_SELECTED - this.selection.selected.length);
    }

    /** ¿Cuántos lugares libres quedan para seleccionar? */
    isCheckboxDisabled(p: Patient): boolean {
        if (this.selection.isSelected(p)) return false;
        return this.remainingCapacity() === 0;
    }

    /** Guardá al chequear filas individuales (respeta el límite). */
    onRowCheckboxChange(checked: boolean, patient: Patient): void {
        if (checked) {
            if (this.remainingCapacity() === 0 && !this.selection.isSelected(patient)) {
                this.alertService.openWarn(this.experienceI18n.translate('modules.schedule.filter.puede-seleccionar-hasta-5-pacientes', undefined, 'Puede seleccionar hasta 5 pacientes'));
                return;
            }
            this.selection.select(patient);
            if (!this.patients.find(p => p.id === patient.id)) {
                this.patients.push(patient);
            }
            this.addAgreementListByPatient(patient);
        } else {
            this.selection.deselect(patient);
            this.removePatient(patient);
        }
    }

    openDialog(title: string, template: TemplateRef<any>, style: { minWidth: string; maxHeight: string }, afterClosed?: () => void): void {
        this._dialogRef = this.dialog.open(DialogComponent, {
            disableClose: true,
            minWidth: style.minWidth,
            maxHeight: style.maxHeight,
            data: { template, title },
        });
    }

    ngOnDestroy() {
        this.providerId = null;
        this.providerPreselected = null;
        this.filteredAllPatients = [];
        this.filteredPatientsWithCases = [];
        this.filteredPatients = null;
        this.filteredProviders = [];
    }

    emitResetScheduleComponent() {
        const agreementsToEmit = [];
        this.onLoadSchedule.emit(agreementsToEmit);
        this.onLoadScheduleWithDataFilter.emit({
            providersSelected: [],
            patientsSelected: [],
            agreementsSelected: agreementsToEmit,
            allPatients: [],
            allAgreements: [],
            allAgreementsList: []
        });
    }
}
